import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  useScrollTrigger,
  Slide,
  useTheme,
  Container
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import { Link } from 'react-router-dom';

const navItems = [
  { label: '首页', href: '/', external: true },
  { label: '我的观察', href: '/#experience' },
  { label: '开发案例', href: '/#tools' },
  { label: '关于我', href: '/#footer' }
];

function HideOnScroll(props) {
  const { children } = props;
  const trigger = useScrollTrigger();

  return (
    <Slide appear={false} direction="down" in={!trigger}>
      {children}
    </Slide>
  );
}

const Navbar = () => {
  const theme = useTheme();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 30;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [scrolled]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const drawer = (
    <Box onClick={handleDrawerToggle} sx={{ textAlign: 'center', py: 2 }}>
      <Typography
        variant="h6"
        component={Link}
        to="/"
        sx={{
          mb: 3,
          color: theme.palette.primary.main,
          fontWeight: 600,
          letterSpacing: '-0.01em',
          textDecoration: 'none',
          '&:hover': {
            opacity: 0.8,
          }
        }}
      >
        地鼠的编程笔记
      </Typography>
      <List>
        {navItems.map((item) => (
          <ListItem
            key={item.label}
            component={item.external ? Link : 'a'}
            to={item.external ? item.href : undefined}
            href={item.external ? undefined : item.href}
            disablePadding
            sx={{
              borderRadius: '12px',
              mx: 2,
              textDecoration: 'none',
              color: 'inherit',
              '&:hover': {
                backgroundColor: 'rgba(255, 152, 0, 0.05)',
              }
            }}
          >
            <ListItemText
              primary={item.label}
              primaryTypographyProps={{
                sx: {
                  textAlign: 'center',
                  py: 1.5,
                  fontWeight: 500,
                  fontSize: '0.9rem',
                }
              }}
            />
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <>
      <HideOnScroll>
        <AppBar 
          position="fixed" 
          elevation={0}
          sx={{ 
            borderBottom: scrolled ? '1px solid rgba(0,0,0,0.05)' : 'none',
            bgcolor: scrolled ? 'rgba(255,255,255,0.97)' : 'transparent',
            backdropFilter: scrolled ? 'saturate(180%) blur(15px)' : 'none',
            transition: 'all 0.3s ease',
          }}
        >
          <Container maxWidth="lg">
            <Toolbar 
              disableGutters
              sx={{ 
                display: 'flex',
                justifyContent: 'space-between',
                py: 1
              }}
            >
              <Typography
                variant="h6"
                component={Link}
                to="/"
                sx={{
                  fontWeight: 600,
                  letterSpacing: '-0.01em',
                  color: '#1D1D1F',
                  textDecoration: 'none',
                  '&:hover': {
                    color: theme.palette.primary.main,
                  }
                }}
              >
                地鼠的编程笔记
              </Typography>
              
              <Box sx={{ 
                display: { xs: 'none', md: 'flex' },
                ml: 'auto',
                '& .MuiButton-root': {
                  mx: 0.5
                }
              }}>
                {navItems.map((item) => (
                  <Button
                    key={item.label}
                    component={item.external ? Link : 'a'}
                    to={item.external ? item.href : undefined}
                    href={item.external ? undefined : item.href}
                    sx={{
                      color: '#1D1D1F',
                      padding: '8px 16px',
                      borderRadius: '9999px',
                      fontSize: '0.9rem',
                      fontWeight: 500,
                      transition: 'all 0.15s ease',
                      textDecoration: 'none',
                      '&:hover': {
                        color: scrolled ? theme.palette.primary.main : theme.palette.primary.main,
                        backgroundColor: 'rgba(0,119,237,0.05)',
                      }
                    }}
                  >
                    {item.label}
                  </Button>
                ))}
              </Box>
              
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="end"
                onClick={handleDrawerToggle}
                sx={{ 
                  display: { md: 'none' },
                  color: '#1D1D1F',
                  ml: 1
                }}
              >
                <MenuIcon />
              </IconButton>
            </Toolbar>
          </Container>
        </AppBar>
      </HideOnScroll>
      
      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { 
            boxSizing: 'border-box', 
            width: 300,
            borderTopLeftRadius: '24px',
            borderBottomLeftRadius: '24px',
            boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
            border: 'none',
            px: 2,
            pt: 2
          },
        }}
      >
        {drawer}
      </Drawer>
    </>
  );
};

export default Navbar; 